import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { networkApi, type AdAccountRow } from '../../api/network.js';
import { EmptyBox, ErrorBox, Field, Pagination, SafeLink, SearchInput, Spinner } from '../../components/ui.js';
import { datetime, text, truncate } from '../../lib/format.js';
import { useUrlSearch, useUrlUpdate } from '../../lib/hooks.js';
import {
  AD_ACCOUNT_STATE_LABELS,
  AD_ACCOUNT_STATE_TONE,
  AD_TYPE_LABELS,
  CodeBadges,
  DetailModal,
  PAY_TYPE_LABELS,
  PER_PAGE,
  descendingNumber,
  labelOf,
} from './shared.js';

/**
 * 계정정보 — 레거시 /xadm/report_network_adinfo.php.
 *
 * 광고 네트워크에 가입한 계정을 적어 둔 표다(ALK_network_adinfo, 65행).
 *
 * **가입비번은 옮기지 않았다.** 원본 테이블에는 65건 중 56건에 평문
 * 비밀번호가 들어 있는데, 원본도 이 목록 화면에는 찍지 않았고(수정 팝업
 * network_adinfo_modify.php 에서만 그대로 보여줬다) 서버도 그 컬럼을
 * 조회하지 않는다. 보관·이관 방식은 아직 정해지지 않은 문제다
 * (todo.md NW-04).
 *
 * 원본 검색칸은 값이 늘 비어 보이는 버그가 있었다(`value="<?=$partnerse?>"`)
 * - 여기서는 지금 걸린 검색어가 그대로 보인다.
 */

function StateBadge({ stats }: { stats: string | null }) {
  if (!stats) return <span className="td-muted">-</span>;
  const tone = AD_ACCOUNT_STATE_TONE[stats];
  // 원본 switch 에 없던 상태값은 뱃지 없이 코드값만 드러낸다 (Cell.tsx 와 같은 규칙).
  if (!tone) return <span className="td-muted">{stats}</span>;
  return <span className={`badge badge--${tone}`}>{labelOf(AD_ACCOUNT_STATE_LABELS, stats)}</span>;
}

export function AdAccountsPage() {
  const [params, setParams] = useSearchParams();
  const update = useUrlUpdate(params, setParams);
  const [q, setQ] = useUrlSearch(params, update, 'q');
  const page = Number(params.get('page') || '1');
  const [detail, setDetail] = useState<AdAccountRow | null>(null);

  const list = useQuery({
    queryKey: ['network', 'adinfo', { q: params.get('q') ?? '', page }],
    queryFn: () => networkApi.adAccounts({ q: params.get('q') || undefined, page, perPage: PER_PAGE }),
    placeholderData: keepPreviousData,
  });

  const data = list.data;

  return (
    <section>
      <header className="page-head">
        <div>
          <h1 className="page-title">
            계정정보
            {list.isFetching && <span className="page-head__busy">갱신 중…</span>}
          </h1>
          <p className="page-desc">
            광고 네트워크 가입 계정 목록입니다. 가입비번은 평문으로 보관돼 있어 이 화면으로 옮기지 않았습니다 —
            필요하면 보관 방식을 먼저 정해야 합니다.
          </p>
        </div>
      </header>

      <div className="filters">
        <Field label="검색 (도메인 · 영문명 · 한글명 · 메모)">
          <SearchInput value={q} placeholder="admaven, 애드메이븐" onChange={setQ} />
        </Field>
        <button
          type="button"
          className="btn btn--ghost filters__reset"
          onClick={() => setParams(new URLSearchParams(), { replace: true })}
        >
          조건 초기화
        </button>
      </div>

      {list.isPending ? (
        <Spinner />
      ) : list.isError ? (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      ) : data && data.items.length === 0 ? (
        <EmptyBox>조건에 맞는 계정이 없습니다.</EmptyBox>
      ) : (
        data && (
          <>
            <div className="table-wrap">
              <table className="table network-table">
                <thead>
                  <tr>
                    <th className="num">번호</th>
                    <th>도메인</th>
                    <th>상태</th>
                    <th>영문명</th>
                    <th>한글명</th>
                    <th>가입이메일</th>
                    <th>가입ID</th>
                    <th>광고 가능 타입</th>
                    <th>결제방식</th>
                    <th>메모</th>
                    <th>등록일</th>
                    <th>상세</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((row, i) => (
                    <tr key={row.seq}>
                      <td className="num td-muted">{descendingNumber(data.total, data.page, data.perPage, i)}</td>
                      <td>
                        <SafeLink href={row.siteurl} className="link">
                          {text(row.siteurl)}
                        </SafeLink>
                      </td>
                      <td>
                        <StateBadge stats={row.stats} />
                      </td>
                      <td>{text(row.nameEn)}</td>
                      <td>{text(row.nameKr)}</td>
                      <td>{text(row.joinEmail)}</td>
                      <td>{text(row.joinId)}</td>
                      <td>
                        <CodeBadges codes={row.adTypes} labels={AD_TYPE_LABELS} />
                      </td>
                      <td>
                        <CodeBadges codes={row.payTypes} labels={PAY_TYPE_LABELS} />
                      </td>
                      <td className="td-clip td-muted" title={row.memo ?? undefined}>
                        {truncate(row.memo, 30)}
                      </td>
                      <td className="td-muted">{datetime(row.regdate)}</td>
                      <td>
                        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDetail(row)}>
                          상세
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onChange={(p) => update({ page: p }, false)}
            />
          </>
        )
      )}

      {detail && (
        <DetailModal
          title={detail.nameKr || detail.nameEn || `계정 #${detail.seq}`}
          onClose={() => setDetail(null)}
          fields={[
            {
              label: '도메인',
              value: (
                <SafeLink href={detail.siteurl} className="link">
                  {text(detail.siteurl)}
                </SafeLink>
              ),
            },
            { label: '상태', value: <StateBadge stats={detail.stats} /> },
            { label: '영문명', value: text(detail.nameEn) },
            { label: '한글명', value: text(detail.nameKr) },
            { label: '가입이메일', value: text(detail.joinEmail) },
            { label: '가입ID', value: text(detail.joinId) },
            { label: '광고 가능 타입', value: <CodeBadges codes={detail.adTypes} labels={AD_TYPE_LABELS} /> },
            { label: '결제방식', value: <CodeBadges codes={detail.payTypes} labels={PAY_TYPE_LABELS} /> },
            { label: '메모', value: text(detail.memo), pre: true },
            { label: '등록일', value: datetime(detail.regdate) },
          ]}
        />
      )}
    </section>
  );
}
